import { Button, UndoSVG } from "@newsela/angelou";
import { actionNames, objectTypes, withTrackEvent } from "@newsela/snitch";
import { useEffect, useState } from "react";

import { useExportablesContext } from "@/components/AIComponents/ExportablesModal/ExportablesContextProvider/useExportablesContext";
import { getOptionalFieldsList } from "@/components/AIComponents/ExportablesModal/utils";
import { transformWordStudyToFormData } from "@/components/AIComponents/ExportablesModal/templates/WordStudyOrganizer/utils";

import type {
  CrosswordPuzzleFormData,
  FactOpinionSorterFormData,
  SourceAnalysisOrganizerFormData,
  WordStudy,
  WordStudyFormData,
} from "@/types/models/Exportables.interface";

const ButtonWithTrackEvent = withTrackEvent(Button);

const ExemplarButton = () => {
  const {
    exportablesMetadata,
    formData,
    setFormData,
    selectedExportable,
    content,
  } = useExportablesContext();

  const [isAutoFilled, setIsAutoFilled] = useState(false);

  const setiIdentifier = selectedExportable?.seti_identifier;

  const optionalFieldList = getOptionalFieldsList(setiIdentifier);

  // Reset toggle when switching templates
  useEffect(() => {
    setIsAutoFilled(false);
  }, [setiIdentifier]);

  const setiData = setiIdentifier
    ? exportablesMetadata?.[setiIdentifier]
    : null;

  if (!setiData) return null;

  const handleAutoFill = () => {
    const currentFormData = formData[setiIdentifier] || {};

    if (setiIdentifier === "primary_source_analysis") {
      const typedFormData = currentFormData as SourceAnalysisOrganizerFormData;
      const typedSetiData = setiData as SourceAnalysisOrganizerFormData;
      setFormData((prev) => ({
        ...prev,
        [setiIdentifier]: {
          ...typedSetiData,
          articleShortTitle: typedFormData.articleShortTitle,
          directions: typedFormData.directions,
        } as SourceAnalysisOrganizerFormData,
      }));
    } else if (setiIdentifier === "word_study") {
      const typedFormData = currentFormData as WordStudyFormData;
      const typedSetiData = setiData as WordStudy;
      const transformedData = transformWordStudyToFormData(
        typedSetiData,
        typedFormData,
      );
      if (transformedData) {
        setFormData((prev) => ({
          ...prev,
          [setiIdentifier]: transformedData,
        }));
      }
    } else if (setiIdentifier === "fact_opinion") {
      const typedFormData = currentFormData as FactOpinionSorterFormData;
      const typedSetiData = setiData as FactOpinionSorterFormData;
      setFormData((prev) => ({
        ...prev,
        [setiIdentifier]: {
          ...typedSetiData,
          articleShortTitle: typedFormData.articleShortTitle,
          directions: typedFormData.directions,
        } as FactOpinionSorterFormData,
      }));
    } else if (setiIdentifier === "crossword_puzzle") {
      const typedFormData = currentFormData as CrosswordPuzzleFormData;
      const typedSetiData = setiData as CrosswordPuzzleFormData;
      const grid = typedSetiData.grid ?? typedFormData.grid ?? [];
      const hintLetters: Record<string, string> = {};

      grid.forEach((row, y) => {
        row.forEach((cell, x) => {
          if (cell.trim() !== "") {
            hintLetters[`${x},${y}`] = cell.toUpperCase();
          }
        });
      });

      setFormData((prev) => ({
        ...prev,
        [setiIdentifier]: {
          ...typedFormData,
          ...typedSetiData,
          hint_letters: hintLetters,
          articleShortTitle: typedFormData.articleShortTitle,
        } as CrosswordPuzzleFormData,
      }));
    }

    setIsAutoFilled(true);
  };

  const handleRestoreToDefault = () => {
    const currentFormData = formData[setiIdentifier] || {};

    if (setiIdentifier === "primary_source_analysis") {
      const typedFormData = currentFormData as SourceAnalysisOrganizerFormData;
      const typedSetiData = setiData as SourceAnalysisOrganizerFormData;
      const restoredData: SourceAnalysisOrganizerFormData = {
        ...typedFormData,
        author: typedSetiData.author,
        year: typedSetiData.year,
      };

      optionalFieldList.forEach((field) => {
        restoredData[field as keyof SourceAnalysisOrganizerFormData] = "";
      });

      setFormData((prev) => ({
        ...prev,
        [setiIdentifier]: restoredData,
      }));
    } else if (setiIdentifier === "word_study") {
      const typedFormData = currentFormData as WordStudyFormData;
      const typedSetiData = setiData as WordStudy;
      const transformedData = transformWordStudyToFormData(
        typedSetiData,
        typedFormData,
      );
      if (transformedData) {
        const restoredData = {
          ...transformedData,
          sentence: "",
        };
        setFormData((prev) => ({
          ...prev,
          [setiIdentifier]: restoredData,
        }));
      }
    } else if (setiIdentifier === "fact_opinion") {
      const typedFormData = currentFormData as FactOpinionSorterFormData;
      const typedSetiData = setiData as FactOpinionSorterFormData;
      const restoredStatements = (typedSetiData.statements ?? []).map(
        ({ statement }) => ({
          statement,
          classification: "",
          explanation: "",
        }),
      );
      setFormData((prev) => ({
        ...prev,
        [setiIdentifier]: {
          ...typedFormData,
          topic: typedSetiData.topic,
          article_title: typedSetiData.article_title,
          statements: restoredStatements,
        } as FactOpinionSorterFormData,
      }));
    } else if (setiIdentifier === "crossword_puzzle") {
      const typedFormData = currentFormData as CrosswordPuzzleFormData;
      setFormData((prev) => ({
        ...prev,
        [setiIdentifier]: {
          ...typedFormData,
          hint_letters: {},
        } as CrosswordPuzzleFormData,
      }));
    }

    setIsAutoFilled(false);
  };

  if (isAutoFilled) {
    return (
      <ButtonWithTrackEvent
        flavor={Button.flavors.muted}
        size={Button.sizes.lg}
        onClick={handleRestoreToDefault}
        icon={{
          SvgComponent: UndoSVG,
          alignment: Button.iconAlignments.right,
        }}
        dataProps={{
          "data-qa-selector": "graphic-organizer-restore-to-default-button",
        }}
        objectType={objectTypes.graphicOrganizer}
        actionName={actionNames.reject}
        streamProperties={{
          content_id: content.id,
          content_type: content.content_type,
        }}
        ariaProps={{
          "aria-label": "Restore graphic organizer template to default",
        }}
      >
        Restore to default
      </ButtonWithTrackEvent>
    );
  }

  return (
    <ButtonWithTrackEvent
      flavor={Button.flavors.primary}
      size={Button.sizes.lg}
      onClick={handleAutoFill}
      dataProps={{
        "data-qa-selector": "graphic-organizer-luna-auto-fill-button",
      }}
      objectType={objectTypes.graphicOrganizer}
      actionName={actionNames.select}
      streamProperties={{
        content_id: content.id,
        content_type: content.content_type,
      }}
      ariaProps={{
        "aria-label":
          "Use Exemplar to fill graphic organizer template with answers",
      }}
    >
      Exemplar
    </ButtonWithTrackEvent>
  );
};

export default ExemplarButton;
